import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import DropdownOptions from "./options/DropdownOptions"
import { useNavigate } from "react-router-dom"
import i18n from "i18n"
import AuthContext from "context/auth/authContext"
import ModalFilterByTag from "./options/ModalFilterByTag"
import { HelveticaBold } from "common/typographies"
import ModalEditMargins from "./options/ModalEditMargins"
import Budget2023Context from "context/budget2023/Budget2023Context"
import DropdownPreferencias from "./TablaPartida/DropdownPreferencias"
import download from "assets/customIcons/download.svg"
import DownloadBudget from "components/Feed/DownloadBudget"
import ModalCerrarPres from "./options/ModalCerrarPres"
import ModalPersonalizarPdf from "./modals/modalPersonalizarPdf"
import ModalCloseCertZeroConfirm from "./modals/ModalCloseCertZeroConfirm"
import CloseTenderModal from "./modals/CloseTenderModal"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import { CUSTOMIZE_PDF } from "constants/entitlements"
import { Box, Button, CircularProgress, TextField } from "@mui/material"
import LockIcon from "@mui/icons-material/Lock"
import LockIconOpen from "@mui/icons-material/LockOpen"
import DownloadIcon from "@mui/icons-material/Download"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import { Typography } from "@material-ui/core"

const LayoutBudget = ({
  children,
  handleSendBudgetModal,
  handleDownloadBudgetModal,
  projectInfo,
  open3,
  setOpen3,
  contactos,
  soyFinal,
  setModificado,
  version,
  index,
  certificaciones,
  budgetSearchQuery,
  setBudgetSearchQuery,
}) => {
  const {
    readyToClose,
    partsChecked,
    selectPartsGroup,
    chaptersChecked,
    budgetTree,
    errorAgrupar,
    groupParts,
    getSelectedBudget2,
    lastActions,
    undoAction,
    closeCertification,
    certDraftPartidaIds,
    closeBudgetExtras,
    openBudgetExtras,
    isBudgetClosed,
    budgetMode,
    loadingTree,
    certification,
  } = useContext(Budget2023Context)
  const chapterCertInProgress = !!certification?.chapterCertInProgress
  const navigate = useNavigate()
  const [openEditMargins, setOpenEditMargins] = useState(false)
  const [openCerrar, setOpenCerrar] = useState(false)
  const [contador, setCont] = useState(0)
  const [showGroup, setShowGroup] = useState(false)
  const [btnLoading, setBtnLoading] = useState(false)
  const [closeCertLoading, setCloseCertLoading] = useState(false)
  const [closeCertZeroConfirmOpen, setCloseCertZeroConfirmOpen] =
    useState(false)
  const [closeTenderModalOpen, setCloseTenderModalOpen] = useState(false)
  const { user, freeTrialExpired, openFreeTrialExpire, getOrganizationNew } =
    useContext(AuthContext)
  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const enableUndo = false

  // No useMemo: budgetTree se muta in-place al certificar; la referencia no cambia y un memo quedaría obsoleto.
  // 1e-15 (borrador en partida.js): cert_current puede ser >0 en árbol pero mostrarse como 0; cert_current>0 alinea con el checkbox.
  let hasAnyPartidaCertCurrent = false
  if (
    certificaciones &&
    budgetTree &&
    typeof budgetTree.preOrder === "function"
  ) {
    for (const cap of budgetTree.preOrder()) {
      for (const p of cap.partidas || []) {
        if (p.contradicted_by_id != null) continue
        const c = parseFloat(p.cert_current)
        if (!Number.isNaN(c) && c > 0) {
          hasAnyPartidaCertCurrent = true
          break
        }
      }
      if (hasAnyPartidaCertCurrent) break
    }
  }

  const hasCertDraftSelection =
    certificaciones && Object.keys(certDraftPartidaIds || {}).length > 0

  const canCloseCertification =
    hasAnyPartidaCertCurrent || hasCertDraftSelection

  const CERT_CURRENT_DRAFT_EPS = 1e-15
  const getPartidasCertCeroCount = () => {
    if (
      !certificaciones ||
      !budgetTree ||
      typeof budgetTree.preOrder !== "function"
    ) {
      return 0
    }
    const idSet = new Set(Object.keys(certDraftPartidaIds || {}))
    for (const cap of budgetTree.preOrder()) {
      for (const p of cap.partidas || []) {
        if (p.contradicted_by_id != null) continue
        const c = parseFloat(p.cert_current)
        if (!Number.isNaN(c) && c > 0 && c <= CERT_CURRENT_DRAFT_EPS) {
          idSet.add(p.partidaId)
        }
      }
    }
    return idSet.size
  }
  const getPartidasACertificarCount = () => {
    if (
      !certificaciones ||
      !budgetTree ||
      typeof budgetTree.preOrder !== "function"
    ) {
      return 0
    }
    const idSet = new Set(Object.keys(certDraftPartidaIds || {}))
    for (const cap of budgetTree.preOrder()) {
      for (const p of cap.partidas || []) {
        if (p.contradicted_by_id != null) continue
        const c = parseFloat(p.cert_current)
        if (!Number.isNaN(c) && c > 0) {
          idSet.add(p.partidaId)
        }
      }
    }
    return idSet.size
  }
  const partidasCertCeroCount = getPartidasCertCeroCount()
  const partidasACertificarCount = getPartidasACertificarCount()

  const handleCloseCertification = async () => {
    setCloseCertLoading(true)
    try {
      const result = await closeCertification(projectInfo.projectId)
      if (result) {
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      }
    } finally {
      setCloseCertLoading(false)
    }
  }

  const handleCloseCertificationButtonClick = () => {
    if (partidasCertCeroCount > 0) {
      setCloseCertZeroConfirmOpen(true)
    } else {
      void handleCloseCertification()
    }
  }

  const handleAcceptCloseCertWithDraft = async () => {
    setCloseCertZeroConfirmOpen(false)
    await handleCloseCertification()
  }

  const handleBudgetLockButtonClick = async () => {
    if (isBudgetClosed) {
      setBtnLoading(true)
      try {
        await openBudgetExtras(projectInfo.projectId)
      } finally {
        setBtnLoading(false)
      }
      return
    }
    if (budgetMode === "tender") {
      setCloseTenderModalOpen(true)
      return
    }
    setBtnLoading(true)
    try {
      await closeBudgetExtras(projectInfo.projectId)
    } finally {
      setBtnLoading(false)
    }
  }

  useEffect(() => {
    selectPartsGroup([], budgetTree.subcapitulos)
    getSelectedBudget2([], budgetTree.subcapitulos)
    if (partsChecked.length > 0 || chaptersChecked.length > 0) {
      setShowGroup(true)
    } else {
      setShowGroup(false)
    }
  }, [partsChecked.length, chaptersChecked.length])

  useEffect(() => {
    if (contador != 0) {
      if (readyToClose == true) {
        if (version) {
          navigate(`/versions/final/${projectInfo.projectId}/${index}`)
        } else {
          setCont(1)
          if (projectInfo.costeGeneral) {
            navigate(`/budgeting/final/${projectInfo.projectId}`)
          } else {
            setOpenEditMargins(readyToClose)
          }
        }
      }
    }
  }, [readyToClose])

  useEffect(() => {
    if (user) {
      getOrganizationNew(user["custom:organizationId"])
    }
  }, [user])

  const [name, setName] = useState("")
  const [openAgrupar, setOpenAgrupar] = useState(false)
  const [openCaps, setOpenCaps] = useState(false)
  const [estadoLista, setEstadoLista] = useState([])
  const [estadoListaCaps, setEstadoListaCaps] = useState([])
  const agruparPartidas = async () => {
    if (errorAgrupar == false) {
      if (estadoLista.length == 0) {
        setName("las partidas")
        setOpenAgrupar(true)
      } else {
        groupParts(projectInfo.projectId)
      }
    } else {
      setOpenSelected(true)
    }
  }

  const agruparCapitulos = async () => {
    if (estadoListaCaps.length == 0) {
      //modal no has agrupado nada
      setName("los capítulos")
      setOpenAgrupar(true)
    } else {
      //antes de esto mensaje de aviso
      setOpenCaps(true)
    }
  }

  const customizePdf = () => {
    if (freeTrialExpired) {
      openFreeTrialExpire()
      return
    }

    if (!checkEntitlement(CUSTOMIZE_PDF, i18n.t("Personalizar Pdf"))) return

    setOpenPersonalizarPdf(true)
  }
  const [openFiltrar, setOpenFiltrar] = useState(false)
  const [openDownloadBudgetModal, setOpenDownloadBudgetModal] = useState(false)
  const [openPersonalizarPdf, setOpenPersonalizarPdf] = useState(false)

  const certToolbarTextButtonSx = {
    color: "#414141",
    textTransform: "none",
    fontWeight: 400,
    fontSize: "14px",
    minHeight: 36,
    textDecoration: "none",
    "&:visited": { color: "#414141" },
    "&:hover": {
      fontWeight: 700,
      color: "#414141",
      textDecoration: "none",
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  }

  const certToolbarIconSx = { fontSize: 20 }

  const budgetSearchField =
    !certificaciones && !loadingTree && user ? (
      <TextField
        size="small"
        value={budgetSearchQuery ?? ""}
        onChange={e => setBudgetSearchQuery(e.target.value)}
        placeholder={i18n.t("Buscar capítulo o partida")}
        inputProps={{
          "data-element": "budgetChapterPartSearch",
          "aria-label": i18n.t("Buscar capítulo o partida"),
        }}
        sx={{
          width: 180,
          marginRight: "10px",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
            height: 32,
            minHeight: 32,
            fontSize: "0.8rem",
            borderRadius: "4px",
          },
          "& .MuiOutlinedInput-input": {
            py: "6px",
            px: 1,
            lineHeight: 1.25,
          },
        }}
      />
    ) : null

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minHeight: "calc(100dvh - 200px)",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box>
            <div>
              <div className="inline">
                {enableUndo && lastActions.length > 0 && (
                  <div
                    id="hoverDropdown"
                    onClick={() => {
                      undoAction()
                    }}
                    style={{
                      display: "flex",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  >
                    <HelveticaBold
                      style={{
                        color: "#414141",
                        marginTop: "6px",
                        width: "120px",
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: "400",
                      }}
                    >
                      {i18n.t("Deshacer acción")}
                    </HelveticaBold>
                  </div>
                )}
              </div>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    component="h1"
                    sx={{ fontWeight: "bold", color: "#414141" }}
                  >
                    {certificaciones
                      ? i18n.t("Certificaciones")
                      : i18n.t("Presupuesto")}
                  </Typography>
                </Box>

                {certificaciones && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    <Button
                      variant="text"
                      component="a"
                      href="https://help.planhopper.com/es/collections/16513149-certificaciones"
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<HelpOutlineIcon sx={certToolbarIconSx} />}
                      sx={certToolbarTextButtonSx}
                    >
                      {i18n.t("invoices.viewHelpArticle")}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        freeTrialExpired
                          ? openFreeTrialExpire()
                          : setOpenDownloadBudgetModal(true)
                      }}
                      startIcon={<DownloadIcon sx={certToolbarIconSx} />}
                      sx={certToolbarTextButtonSx}
                      data-testid="budget-certification-download-button"
                    >
                      {i18n.t("Descargar")}
                    </Button>
                    <Button
                      variant="contained"
                      color="branded"
                      data-element="closeCertificationBtn"
                      disabled={
                        closeCertLoading ||
                        !canCloseCertification ||
                        chapterCertInProgress
                      }
                      sx={{
                        width: "200px",
                        height: "36px",
                      }}
                      onClick={handleCloseCertificationButtonClick}
                    >
                      {closeCertLoading || chapterCertInProgress ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        i18n.t("Cerrar certificación")
                      )}
                    </Button>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {user ? (
                    user.freeTrialExpired ||
                    user["custom:role"] === "WORKER" ? (
                      budgetSearchField
                    ) : (
                      <>
                        {!version ? (
                          <>
                            {
                              // Botón de descarga de presupuesto
                              certificaciones ? null : (
                                <>
                                  {loadingTree ? null : (
                                    <>
                                      {budgetMode === "tender" && (
                                        <div
                                          style={{
                                            marginTop: "5px",
                                            marginRight: "10px",
                                            fontWeight: "500",
                                          }}
                                        >
                                          {i18n.t("editProject.tenderMode")}
                                        </div>
                                      )}
                                      {budgetSearchField}
                                      <Button
                                        color={
                                          isBudgetClosed ? "error" : "primary"
                                        }
                                        variant="contained"
                                        startIcon={
                                          btnLoading ? null : isBudgetClosed ? (
                                            <LockIcon />
                                          ) : (
                                            <LockIconOpen />
                                          )
                                        }
                                        sx={{
                                          minWidth: "122px",
                                          height: "32px",
                                          marginRight: "10px",
                                        }}
                                        onClick={handleBudgetLockButtonClick}
                                        size="small"
                                      >
                                        {btnLoading ? (
                                          <CircularProgress
                                            size={20}
                                            color="inherit"
                                          />
                                        ) : isBudgetClosed ? (
                                          "Cerrado"
                                        ) : (
                                          "Abierto"
                                        )}
                                      </Button>
                                    </>
                                  )}

                                  <DropdownPreferencias />
                                  <div
                                    id="hoverDropdown"
                                    onClick={() => {
                                      freeTrialExpired
                                        ? openFreeTrialExpire()
                                        : setOpenDownloadBudgetModal(true)
                                    }}
                                    style={{
                                      display: "flex",
                                      cursor: "pointer",
                                    }}
                                    data-testid="budget-download-button"
                                  >
                                    <img
                                      src={download}
                                      style={{
                                        color: "black",
                                        marginTop: "-10px",
                                        width: "12px",
                                        marginRight: "4px",
                                        fontWeight: "400",
                                      }}
                                    />
                                    <HelveticaBold
                                      style={{
                                        color: "#414141",
                                        marginTop: "6px",
                                        width: "85px",
                                        textTransform: "none",
                                        fontSize: "14px",
                                        fontWeight: "400",
                                      }}
                                    >
                                      {i18n.t("Descargar")}
                                    </HelveticaBold>
                                  </div>
                                </>
                              )
                            }
                          </>
                        ) : null}
                      </>
                    )
                  ) : null}
                </Box>

                {!certificaciones && (
                  <Box sx={{ flexShrink: 0, ml: 1 }}>
                    <DropdownOptions
                      open2={openEditMargins}
                      setOpen2={setOpenEditMargins}
                      projectInfo={projectInfo}
                      soyFinal={soyFinal}
                      contactos={contactos}
                      handleSendBudgetModal={handleSendBudgetModal}
                      handleDownloadBudgetModal={handleDownloadBudgetModal}
                      agruparPartidas={agruparPartidas}
                      agruparCapitulos={agruparCapitulos}
                      setEstadoLista={setEstadoLista}
                      setEstadoListaCaps={setEstadoListaCaps}
                      openAgrupar={openAgrupar}
                      setOpenAgrupar={setOpenAgrupar}
                      name={name}
                      openCaps={openCaps}
                      setOpenCaps={setOpenCaps}
                      setOpenFiltrar={setOpenFiltrar}
                      version={version}
                      onPersonalizarPdf={customizePdf}
                    />
                  </Box>
                )}
              </Box>
            </div>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Box>

      <ModalEditMargins
        projectInfo={projectInfo}
        open={openEditMargins}
        setOpen={setOpenEditMargins}
      />
      <ModalFilterByTag open={openFiltrar} setOpen={setOpenFiltrar} />
      <DownloadBudget
        open={openDownloadBudgetModal}
        setOpen={setOpenDownloadBudgetModal}
        name={projectInfo.name}
        soyFinal={soyFinal}
        fromTab={
          certificaciones
            ? DownloadBudgetEnum.FROM_CERTIFICATION
            : DownloadBudgetEnum.DEFAULT
        }
      />
      <ModalCerrarPres
        projectInfo={projectInfo}
        openCerrar={openCerrar}
        setOpenCerrar={setOpenCerrar}
        open3={open3}
        setOpen3={setOpen3}
        contactos={contactos}
      />
      {openPersonalizarPdf && (
        <ModalPersonalizarPdf
          open={openPersonalizarPdf}
          setOpen={setOpenPersonalizarPdf}
          projectInfo={projectInfo}
        />
      )}
      <ModalCloseCertZeroConfirm
        open={closeCertZeroConfirmOpen}
        onClose={() => setCloseCertZeroConfirmOpen(false)}
        onAccept={() => void handleAcceptCloseCertWithDraft()}
        partidaCount={partidasCertCeroCount}
        totalPartidasCount={partidasACertificarCount}
      />
      <CloseTenderModal
        open={closeTenderModalOpen}
        onClose={() => setCloseTenderModalOpen(false)}
        onConfirmCloseBudget={async tenderCloseQuery => {
          await closeBudgetExtras(projectInfo.projectId, tenderCloseQuery)
        }}
      />
      {entitlementDialog}
    </>
  )
}
LayoutBudget.propTypes = {
  handleSendBudgetModal: PropTypes.func.isRequired,
  handleDownloadBudgetModal: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  projectInfo: PropTypes.object.isRequired,
  addCapitulo: PropTypes.func.isRequired,
  open3: PropTypes.bool.isRequired,
  setOpen3: PropTypes.func.isRequired,
  contactos: PropTypes.any.isRequired,
  soyFinal: PropTypes.bool,

  setModificado: PropTypes.func,
  version: PropTypes.object,
  index: PropTypes.number,
  certificaciones: PropTypes.bool,
  budgetSearchQuery: PropTypes.string,
  setBudgetSearchQuery: PropTypes.func,
}
export default LayoutBudget
